import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { blogPosts } from "@/data/blog";
import SEOHead from "@/components/SEOHead";

export default function BlogPage() {
  const featured = blogPosts[0];
  const rest = blogPosts.slice(1);

  return (
    <main data-testid="blog-page">
      <SEOHead
        title="Engineering Blog — Venury"
        description="Technical deep-dives and lessons from building production systems at scale. AI, FinTech, trading, compliance, and more."
        url="https://venury.tech/blog"
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ top: "-20%", right: "-10%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
            <span className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em]">Blog</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight"
          >
            Engineering Insights
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg text-slate-400 leading-relaxed max-w-xl mt-6"
          >
            Technical deep-dives and lessons from building production systems at scale.
          </motion.p>
        </div>
      </section>

      {/* Featured Post */}
      {featured && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <ScrollReveal>
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="featured-blog-post"
                className="block glass-card rounded-lg overflow-hidden group"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="relative h-64 lg:h-auto overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#1a252f]/50 hidden lg:block" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent lg:hidden" />
                  </div>
                  <div className="p-8 lg:p-12 flex flex-col justify-center">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-teal-bright mb-4">Featured</span>
                    <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-teal-bright transition-colors duration-200 leading-snug">
                      {featured.title}
                    </h2>
                    <p className="text-base text-slate-400 leading-relaxed mb-6">
                      {featured.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-600">
                      <span>{featured.author}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" />
                      <span>{featured.readTime}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" />
                      <span>{new Date(featured.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-2xl font-bold text-white tracking-tight mb-12">
              All Articles
            </h2>
          </ScrollReveal>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerDelay={0.08}>
            {rest.map((post) => (
              <StaggerItem key={post.slug}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="block glass-card rounded-lg overflow-hidden group h-full"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a252f] to-transparent" />
                    <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-wider bg-teal-primary/20 text-teal-bright px-3 py-1 rounded backdrop-blur-sm">
                      {post.category}
                    </span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-heading text-base font-semibold text-white mb-2 group-hover:text-teal-bright transition-colors duration-200 leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-slate-600 pt-4 border-t border-white/5">
                      <span>{post.readTime}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-600" />
                      <span>{new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 md:py-28 bg-[#080C10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <div className="max-w-2xl mx-auto text-center">
              <span className="font-mono text-sm text-teal-bright uppercase tracking-widest block mb-4">Newsletter</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
                Engineering insights, delivered monthly.
              </h2>
              <p className="text-base text-slate-400 leading-relaxed mb-8">
                One email per month. Technical deep-dives, industry analysis, and lessons 
                from the field. No fluff, no spam.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  data-testid="newsletter-email-input"
                  placeholder="you@company.com"
                  className="flex-1 px-4 py-3 rounded bg-white/5 border border-white/10 text-white placeholder-slate-600 font-mono text-sm focus:outline-none focus:border-teal-primary/50 transition-colors"
                />
                <button
                  data-testid="newsletter-subscribe-btn"
                  className="px-6 py-3 bg-teal-primary hover:bg-teal-dark text-white font-semibold text-sm rounded transition-colors duration-200 whitespace-nowrap"
                >
                  Subscribe
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-4">Join 2,000+ engineering leaders.</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </main>
  );
}
